import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib';

/**
 * Defines the visual styles for the Button component.
 */
const buttonVariants = cva(
  `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md
  text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50
  [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0
  [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50
  focus-visible:ring-[3px] aria-invalid:ring-destructive/20
  dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive`,
  {
    variants: {
      variant: {
        default: 'bg-primary/80 text-primary-50 shadow-xs hover:bg-primary/90',
        destructive: `
          bg-destructive text-white shadow-xs hover:bg-destructive/90
          focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40
          dark:bg-destructive/60
        `,
        outline: `
          border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground
          dark:bg-input/30 dark:border-input dark:hover:bg-input/50
        `,
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-nav-link-text underline-offset-4 hover:underline',
        navLink: 'form-link-action',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        tiny: 'font-regular h-6 rounded-sm space-x-2 text-xs px-2 py-2 has-[>svg]:px-1.5',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

/**
 * A flexible button component with multiple variants and sizes.
 */
function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    /** Change the default rendered element for the one passed as a child, merging their props and behavior. */
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      data-slot="button"
      {...props}
    />
  );
}

export { Button, buttonVariants };
